/**
 * Firebase Analytics Events
 *
 * Arquivo central para todos os eventos de analytics da plataforma iSugar.
 * Utiliza logEvent do Firebase Analytics para rastrear comportamento do usuário.
 */

import { logEvent } from "firebase/analytics";
import { myAnalytics } from "../contexts/firebaseProvider";

// ============================================================================
// ONBOARDING EVENTS
// ============================================================================

/**
 * Disparar quando o usuário inicia o processo de onboarding/registro
 */
export const trackOnboardingStarted = () => {
  logEvent(myAnalytics, "onboarding_started");
};

/**
 * Disparar quando o usuário completa todo o processo de onboarding
 */
export const trackOnboardingCompleted = () => {
  logEvent(myAnalytics, "onboarding_completed");
};

// ============================================================================
// PROFILE EVENTS
// ============================================================================

/**
 * Disparar quando um novo perfil é criado com sucesso
 * @param {string} userId - ID do usuário que criou o perfil
 * @param {string} role - Função escolhida (sugar_daddy, sugar_mommy, sugar_baby)
 */
export const trackProfileCreated = (userId, role) => {
  logEvent(myAnalytics, "profile_created", {
    userId,
    role,
  });
};

/**
 * Disparar quando o perfil está 100% preenchido
 * @param {string} userId - ID do usuário
 */
export const trackProfileCompleted = (userId) => {
  logEvent(myAnalytics, "profile_completed", {
    userId,
  });
};

/**
 * Disparar quando o usuário faz upload de uma foto de perfil
 * @param {string} userId - ID do usuário
 * @param {boolean} isFirstPhoto - Se é a primeira foto ou não
 */
export const trackPhotoUploaded = (userId, isFirstPhoto = false) => {
  logEvent(myAnalytics, "photo_uploaded", {
    userId,
    isFirstPhoto,
  });
};

/**
 * Disparar quando o usuário seleciona seus interesses
 * @param {string} userId - ID do usuário
 * @param {number} interestsCount - Quantidade de interesses selecionados
 */
export const trackInterestsSelected = (userId, interestsCount) => {
  logEvent(myAnalytics, "interests_selected", {
    userId,
    interestsCount,
  });
};

/**
 * Disparar quando o usuário concede permissão de localização
 * @param {string} userId - ID do usuário
 */
export const trackLocationPermissionGranted = (userId) => {
  logEvent(myAnalytics, "location_permission_granted", {
    userId,
  });
};

// ============================================================================
// ENGAGEMENT EVENTS
// ============================================================================

/**
 * Disparar quando o usuário dá swipe right (gosta) em um perfil
 * @param {string} targetUserId - ID do usuário que recebeu o like
 */
export const trackSwipeRight = (targetUserId) => {
  logEvent(myAnalytics, "swipe_right", {
    targetUserId,
  });
};

/**
 * Disparar quando o usuário dá swipe left (rejeita) em um perfil
 * @param {string} targetUserId - ID do usuário que foi rejeitado
 */
export const trackSwipeLeft = (targetUserId) => {
  logEvent(myAnalytics, "swipe_left", {
    targetUserId,
  });
};

/**
 * Disparar quando o usuário visualiza um perfil completo
 * @param {string} targetUserId - ID do usuário cujo perfil foi visualizado
 */
export const trackProfileViewed = (targetUserId) => {
  logEvent(myAnalytics, "profile_viewed", {
    targetUserId,
  });
};

/**
 * Disparar após o backend confirmar que ocorreu um match
 * @param {string} userId - ID do usuário atual
 * @param {string} targetUserId - ID do usuário com quem ocorreu o match
 */
export const trackMatchCreated = (userId, targetUserId) => {
  logEvent(myAnalytics, "match_created", {
    userId,
    targetUserId,
  });
};

/**
 * Disparar quando o usuário envia uma mensagem
 * @param {string} conversationId - ID da conversa
 * @param {string} targetUserId - ID do destinatário
 */
export const trackMessageSent = (conversationId, targetUserId) => {
  logEvent(myAnalytics, "message_sent", {
    conversationId,
    targetUserId,
  });
};

/**
 * Disparar quando o usuário recebe uma mensagem (opcional)
 * @param {string} conversationId - ID da conversa
 * @param {string} fromUserId - ID do remetente
 */
export const trackMessageReceived = (conversationId, fromUserId) => {
  logEvent(myAnalytics, "message_received", {
    conversationId,
    fromUserId,
  });
};

// ============================================================================
// SESSION EVENTS
// ============================================================================

/**
 * Disparar quando o app é aberto/carregado
 */
export const trackAppOpen = () => {
  logEvent(myAnalytics, "app_open");
};

/**
 * Disparar quando o app é fechado (se aplicável)
 */
export const trackAppClose = () => {
  logEvent(myAnalytics, "app_close");
};

/**
 * Disparar quando a sessão do usuário termina
 */
export const trackSessionEnd = () => {
  logEvent(myAnalytics, "session_end");
};

// ============================================================================
// AUTH / SESSION LIFECYCLE EVENTS
// ----------------------------------------------------------------------------
// Estes eventos existem para tornar OBSERVÁVEL o ciclo de vida da autenticação
// (login, logout, expiração de sessão, falha de refresh de token). Sem eles é
// impossível saber se usuários estão sendo deslogados silenciosamente ou montar
// uma curva de retenção confiável.
// ============================================================================

/**
 * Disparar quando o login é concluído com sucesso.
 * @param {string} userId
 * @param {string} [method="email_password"]
 */
export const trackLoginSuccess = (userId, method = "email_password") => {
  logEvent(myAnalytics, "login_success", { userId, method });
};

/**
 * Disparar quando uma tentativa de login falha.
 * @param {string} reason - código/motivo do erro (ex: auth/wrong-password)
 */
export const trackLoginFailed = (reason) => {
  logEvent(myAnalytics, "login_failed", { reason });
};

/**
 * Disparar quando o usuário faz logout.
 * @param {string} [userId]
 * @param {string} [reason="user_initiated"] - user_initiated | cross_tab | forced
 */
export const trackLogout = (userId, reason = "user_initiated") => {
  logEvent(myAnalytics, "logout", { userId, reason });
};

/**
 * Disparar quando o usuário é deslogado à força (ex: refresh de token falhou
 * após um 401 e não foi possível recuperar a sessão).
 * @param {string} reason
 */
export const trackForceLogout = (reason = "token_refresh_failed") => {
  logEvent(myAnalytics, "force_logout", { reason });
};

/**
 * Disparar quando a renovação do ID token do Firebase falha.
 * @param {Error|object} [error]
 * @param {string} [context] - onde ocorreu (boot | onIdTokenChanged | interceptor)
 */
export const trackTokenRefreshFailed = (error, context = "unknown") => {
  logEvent(myAnalytics, "token_refresh_failed", {
    context,
    error: error?.message || String(error || "unknown"),
  });
};

/**
 * Disparar quando o usuário solicita recuperação de senha. Permite medir quantos
 * usuários que voltam ficam presos por não conseguir redefinir a senha.
 */
export const trackPasswordResetRequested = () => {
  logEvent(myAnalytics, "password_reset_requested");
};

/**
 * Disparar quando o gate de verificação de e-mail é exibido (usuário logado mas
 * com e-mail não verificado). Mede o tamanho real desse funil de fricção.
 */
export const trackEmailVerificationGateShown = () => {
  logEvent(myAnalytics, "email_verification_gate_shown");
};

// ============================================================================
// CONVERSION EVENTS (Monetização)
// ============================================================================

/**
 * Disparar quando o usuário inicia o processo de compra
 * @param {string} planId - ID do plano selecionado
 */
export const trackPurchaseStarted = (planId) => {
  logEvent(myAnalytics, "purchase_started", {
    planId,
  });
};

/**
 * Disparar quando a compra é concluída com sucesso
 * @param {string} planId - ID do plano comprado
 * @param {number} price - Valor pago
 * @param {string} currency - Moeda (ex: BRL)
 */
export const trackPurchaseCompleted = (planId, price, currency = "BRL") => {
  logEvent(myAnalytics, "purchase_completed", {
    planId,
    price,
    currency,
  });
};

/**
 * Disparar quando o usuário cancela sua assinatura
 * @param {string} planId - ID do plano cancelado
 */
export const trackSubscriptionCanceled = (planId) => {
  logEvent(myAnalytics, "subscription_canceled", {
    planId,
  });
};

/**
 * Disparar quando o usuário clica em um botão de upgrade
 * @param {string} currentPlan - Plano atual do usuário
 * @param {string} targetPlan - Plano para o qual deseja fazer upgrade
 */
export const trackUpgradeClicked = (currentPlan, targetPlan) => {
  logEvent(myAnalytics, "upgrade_clicked", {
    currentPlan,
    targetPlan,
  });
};

/**
 * Disparar quando o usuário inicia navegação para a página de planos ou checkout.
 * Permite identificar de qual ponto do app o usuário está convertendo.
 *
 * @param {string} source      - Identificador do ponto de entrada. Valores possíveis:
 *   "limit_modal"             – Modal de limite atingido (ver planos)
 *   "limit_modal_direct"      – Modal de limite atingido (checkout rápido)
 *   "daily_limits_widget"     – Widget de limites diários (botão Premium)
 *   "daily_limits_widget_direct" – Widget de limites diários (checkout rápido via modal)
 *   "premium_feature_gate"    – Gate de feature bloqueada para free
 *   "who_liked_me"            – Widget "Quem me curtiu"
 *   "profile_visitors"        – Widget de visitantes do perfil
 *   "upgrade_cta"             – Componente UpgradeCTA
 *   "feed_post_limit_direct"  – Feed: checkout direto ao atingir limite de post
 *   "profile_details"         – Página de detalhes de perfil
 *   "chat_message_limit"      – Conversa de chat (limite de mensagens)
 *   "rating_system"           – Sistema de reputação/avaliações
 *   "user_dashboard"          – Painel do usuário
 *   "gift_marketplace"        – Marketplace de presentes
 *   "match_requests"          – Lista de solicitações de match
 *   "subscription_details"    – Detalhes de assinatura no perfil
 *   "new_conversation_paywall" – Modal de conexão gratuita semanal esgotada
 *   "chat_media_send_paywall" – Usuário Free tentou enviar foto no chat
 *   "chat_media_view_paywall" – Usuário Free tentou visualizar foto recebida no chat
 *
 * @param {object} [extraProps] - Contexto adicional (ex: limitType, featureName, planType)
 */
export const trackSubscriptionEntryPoint = (source, extraProps = {}) => {
  logEvent(myAnalytics, "subscription_entry_point", {
    source,
    ...extraProps,
  });
};

// ============================================================================
// CONEXÃO GRATUITA SEMANAL (FREE — 1 conversa nova a cada 7 dias)
// ============================================================================

/**
 * Disparar quando um usuário Free inicia com sucesso sua conversa gratuita da semana
 * @param {string} targetUserId - ID do usuário com quem a conversa foi iniciada
 */
export const trackFreeWeeklyConnectionStarted = (targetUserId) => {
  logEvent(myAnalytics, "free_weekly_connection_started", { targetUserId });
};

/**
 * Disparar quando um usuário Free tenta iniciar uma 2ª conversa antes da janela de 7 dias
 */
export const trackFreeWeeklyConnectionLimit = () => {
  logEvent(myAnalytics, "free_weekly_connection_limit");
};

/**
 * Disparar quando o modal de conexão gratuita esgotada é exibido
 */
export const trackFreeWeeklyConnectionPaywall = () => {
  logEvent(myAnalytics, "free_weekly_connection_paywall");
};

/**
 * Disparar quando o usuário clica em "Assinar Premium" no modal de conexão gratuita
 */
export const trackFreeWeeklyConnectionUpgradeClick = () => {
  logEvent(myAnalytics, "free_weekly_connection_upgrade_click");
};

// ============================================================================
// SECURITY/MODERATION EVENTS
// ============================================================================

/**
 * Disparar quando o usuário reporta outro usuário
 * @param {string} reportedUserId - ID do usuário reportado
 * @param {string} reason - Motivo do report
 */
export const trackUserReported = (reportedUserId, reason) => {
  logEvent(myAnalytics, "user_reported", {
    reportedUserId,
    reason,
  });
};

/**
 * Disparar quando o usuário bloqueia outro usuário
 * @param {string} blockedUserId - ID do usuário bloqueado
 */
export const trackUserBlocked = (blockedUserId) => {
  logEvent(myAnalytics, "user_blocked", {
    blockedUserId,
  });
};

/**
 * Disparar quando o usuário desfaz o bloqueio de outro usuário
 * @param {string} blockedUserId - ID do usuário desbloqueado
 */
export const trackUserUnblocked = (blockedUserId) => {
  logEvent(myAnalytics, "user_unblocked", {
    blockedUserId,
  });
};

// ============================================================================
// PRIVATE PHOTOS EVENTS
// ============================================================================

/** Disparar quando o usuário adiciona uma foto privada ao próprio perfil */
export const trackPrivatePhotoAdded = (total) => {
  logEvent(myAnalytics, "photo_private_added", { total });
};

/** Disparar quando um usuário solicita acesso às fotos privadas de um perfil */
export const trackPrivatePhotoRequest = (ownerId) => {
  logEvent(myAnalytics, "photo_private_request", { ownerId });
};

/** Disparar quando o dono aprova uma solicitação de fotos privadas */
export const trackPrivatePhotoApproved = (requesterId) => {
  logEvent(myAnalytics, "photo_private_approved", { requesterId });
};

/** Disparar quando o dono nega uma solicitação de fotos privadas */
export const trackPrivatePhotoDenied = (requesterId) => {
  logEvent(myAnalytics, "photo_private_denied", { requesterId });
};

/** Disparar quando um usuário visualiza fotos privadas de um perfil */
export const trackPrivatePhotoView = (ownerId) => {
  logEvent(myAnalytics, "photo_private_view", { ownerId });
};

/** Disparar quando o paywall de fotos privadas é exibido a um usuário Free */
export const trackPrivatePhotoPaywall = (ownerId) => {
  logEvent(myAnalytics, "photo_private_paywall", { ownerId });
};

/** Disparar quando o usuário clica no banner de lançamento de fotos privadas */
export const trackPrivatePhotoBannerClick = () => {
  logEvent(myAnalytics, "photo_private_banner_click");
};

// ============================================================================
// CHAT MEDIA EVENTS (módulo "Mídias do Chat" — fotos normais e temporárias)
// ============================================================================

/** Disparar quando uma foto é enviada com sucesso no chat */
export const trackChatMediaSent = (chatId) => {
  logEvent(myAnalytics, "chat_media_sent", { chatId });
};

/** Disparar quando uma foto (normal ou temporária) é aberta/visualizada no chat */
export const trackChatMediaViewed = (mediaId) => {
  logEvent(myAnalytics, "chat_media_viewed", { mediaId });
};

/** Disparar quando o paywall de mídias do chat é exibido a um usuário Free */
export const trackChatMediaPaywallShown = (context) => {
  logEvent(myAnalytics, "chat_media_paywall_shown", { context });
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Função auxiliar para disparar eventos personalizados
 * @param {string} eventName - Nome do evento
 * @param {object} params - Parâmetros do evento
 */
export const trackCustomEvent = (eventName, params = {}) => {
  logEvent(myAnalytics, eventName, params);
};
